import type {
  AgentThreadToolUseContent,
  ClientToolResult,
} from "../AgentThreadRecord";

import type { ApprovalMode } from "./approvalModes";
import { needsApproval, resolveApprovalMode } from "./approvalModes";
import type {
  ClientTool,
  ClientToolBatchScope,
  ClientToolDeps,
} from "./ClientTools";
import {
  ToolErrorKind,
  classifyToolFailure,
  describeToolFailure,
  toolError,
} from "./ClientTools";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingToolApproval {
  toolUse: AgentThreadToolUseContent;
  tool: ClientTool;
}

export interface BatchRunnerCallbacks {
  /** Called whenever the current pending approval changes (including to null). */
  onPendingApprovalChanged: (pending: PendingToolApproval | null) => void;

  /** Called when all tools in the batch have resolved and results are ready to submit. */
  onBatchComplete: (sessionId: string, results: ClientToolResult[]) => void;
}

export interface BatchRunConfig {
  sessionId: string;
  toolUses: AgentThreadToolUseContent[];
  autoEnabled: boolean;
  userPreferences: Record<string, ApprovalMode> | null | undefined;
  deps: ClientToolDeps;
  resolveClientTool: (toolName: string) => ClientTool | undefined;
}

// ---------------------------------------------------------------------------
// Result helpers
// ---------------------------------------------------------------------------

function buildToolResult(
  toolUse: AgentThreadToolUseContent,
  result: { response: Record<string, unknown>; success: boolean },
  runtimeMs: number,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: runtimeMs,
    status: result.success ? "success" : "error",
    output: result.response,
  };
}

function buildDeclinedResult(
  toolUse: AgentThreadToolUseContent,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "error",
    output: { message: "User declined to execute this tool." },
  };
}

/**
 * Result for a tool whose approval prompt went off screen before anyone could
 * answer it.
 *
 * Phrased so the model treats it as an interruption rather than a refusal: the
 * tool never ran and nothing about the request was wrong, so re-requesting is
 * the right move. A declined result would teach it the opposite.
 */
function buildInterruptedResult(
  toolUse: AgentThreadToolUseContent,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "error",
    output: {
      message:
        "Navigation closed this conversation view before the user could " +
        "approve this tool, so it did not run. Request it again if it is " +
        "still needed.",
    },
  };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Pure state machine for batch client-tool execution with approval gating.
 *
 * Manages the triage (auto-execute vs queue for approval), approval queue,
 * result accumulation, and "batch complete" detection. No React, no hooks,
 * no DOM — all interaction with the component tree happens via callbacks.
 *
 * This is the only path from a `tool_use` to a tool's `execute`, which is what
 * makes the approval gate unavoidable rather than a convention the UI upholds:
 * a REQUIRE_APPROVAL tool cannot run without an `approve()` call, and it runs
 * at most once per tool_use_id however many times a batch is re-entered.
 *
 * The hook `useClientToolExecution` creates a runner and acts as a thin
 * adapter that bridges React context (services, navigation, state) to
 * the runner's lifecycle methods.
 */
export class ClientToolBatchRunner {
  private pendingQueue: PendingToolApproval[] = [];
  private accumulatedResults: ClientToolResult[] = [];
  private claimedToolCount = 0;
  private batchSessionId: string | null = null;
  private currentApproval: PendingToolApproval | null = null;
  /**
   * Every tool_use_id this runner has taken responsibility for, across batches.
   *
   * Deliberately outlives {@link reset}. The effect that drives the runner
   * re-fires on any change to the thread record — including the `roboto`
   * message the server appends mid-turn with partial server-tool results — and
   * those re-entries carry the same tool_use_ids. This set is what makes them
   * no-ops instead of a second `client_create_collection`.
   */
  private handledToolUseIds = new Set<string>();
  /**
   * What the tools of the current batch have told each other.
   *
   * The runner owns it because it is the only thing that knows where one batch
   * ends: the hook rebuilds its deps every render (so state kept there is
   * either stale or shared with React), and a module-level object would carry
   * a navigation from one batch into the next. Replaced wholesale by
   * {@link reset}, so a tool can never read a flag set turns ago.
   */
  private batchScope: ClientToolBatchScope = {};
  /**
   * Set once this runner's surface is gone and can no longer host an approval.
   *
   * Consulted during triage as well as by {@link detach}, because the unmount
   * can land while an auto-executed tool is still awaited — the navigating
   * tool that caused it, typically. Triage reaches the gated tools after that
   * await, and without this flag would queue them onto a runner nobody is
   * listening to.
   */
  private detached = false;
  private callbacks: BatchRunnerCallbacks;

  constructor(callbacks: BatchRunnerCallbacks) {
    this.callbacks = callbacks;
  }

  get pendingApproval(): PendingToolApproval | null {
    return this.currentApproval;
  }

  /**
   * Take in a batch of tool uses. Auto-executes the ones that don't need
   * approval, queues the ones that do. Fires `onPendingApprovalChanged` when a
   * tool needs approval, or `onBatchComplete` once every claimed tool_use has
   * a result.
   *
   * Re-entrant by tool_use_id, not by call: ids already handled are dropped, so
   * calling this again with a batch that is still in flight neither re-runs a
   * write nor discards results already collected. Ids not seen before join the
   * batch in flight — a queued approval stays on screen and the newcomers line
   * up behind it.
   */
  async startBatch(config: BatchRunConfig): Promise<void> {
    if (config.sessionId !== this.batchSessionId || !this.hasUnsettledWork()) {
      this.reset();
    }
    this.batchSessionId = config.sessionId;

    const fresh = config.toolUses.filter(
      (toolUse) => !this.handledToolUseIds.has(toolUse.tool_use_id),
    );
    if (fresh.length === 0) {
      return;
    }
    // Claim the whole set before the first await. `executeAndRecord` yields,
    // and a re-entry landing in that window must not pick up a tool_use this
    // call is already running.
    for (const toolUse of fresh) {
      this.handledToolUseIds.add(toolUse.tool_use_id);
    }
    this.claimedToolCount += fresh.length;

    const needsApprovalQueue: PendingToolApproval[] = [];

    for (const toolUse of fresh) {
      const tool = config.resolveClientTool(toolUse.tool_name);

      if (!tool) {
        this.accumulatedResults.push(
          buildToolResult(
            toolUse,
            toolError(
              `Unknown client tool: ${toolUse.tool_name}`,
              {},
              // Naming a tool that isn't registered is not a transient
              // failure; the same call fails identically.
              ToolErrorKind.VALIDATION,
            ),
            0,
          ),
        );
        continue;
      }

      const mode = resolveApprovalMode(tool, config.userPreferences);

      if (needsApproval(mode, config.autoEnabled)) {
        if (this.detached) {
          this.accumulatedResults.push(buildInterruptedResult(toolUse));
          continue;
        }
        needsApprovalQueue.push({ toolUse, tool });
      } else {
        await this.executeAndRecord(toolUse, tool, this.scoped(config.deps));
      }
    }

    this.pendingQueue.push(...needsApprovalQueue);
    if (this.currentApproval) {
      // An approval is already on screen. Whatever arrived waits its turn, and
      // completion is decided when that approval resolves.
      return;
    }

    const next = this.pendingQueue.shift();
    if (next) {
      this.currentApproval = next;
      this.callbacks.onPendingApprovalChanged(next);
    } else if (this.accumulatedResults.length === this.claimedToolCount) {
      this.completeBatch();
    }
  }

  /**
   * User approved the currently pending tool. Executes it, records the
   * result, and advances the queue. `deps` is passed at call time so the
   * hook can provide fresh values (e.g., current route for isOnVisualizerPage).
   */
  async approve(deps: ClientToolDeps): Promise<void> {
    if (!this.currentApproval) {
      return;
    }

    const { toolUse, tool } = this.currentApproval;
    // Claim the approval *before* awaiting. `executeAndRecord` is async, so a
    // second call arriving while it is in flight would otherwise find
    // `currentApproval` still set and run the same tool_use again — two
    // collections created, the second result discarded once the batch
    // completes. The UI also latches its Approve button; this is the half that
    // holds regardless of which surface calls in.
    this.currentApproval = null;
    // The caller's deps are fresh, but they carry no batch scope of their own:
    // an approval-gated tool sitting between two auto-executed ones has to see
    // the same scope they did.
    await this.executeAndRecord(toolUse, tool, this.scoped(deps));
    this.advanceQueue();
  }

  /**
   * User declined the currently pending tool. Records a declined result
   * and advances the queue.
   */
  decline(): void {
    if (!this.currentApproval) {
      return;
    }

    this.accumulatedResults.push(
      buildDeclinedResult(this.currentApproval.toolUse),
    );
    this.advanceQueue();
  }

  /**
   * This runner's surface is going away; answer whatever it still owes.
   *
   * A tool that navigates takes its own surface off screen, and the batch's
   * tool_use_ids are claimed process-wide for the life of the tab, so the
   * surface that inherits the thread cannot pick the work up. Anything left
   * queued here would therefore never be answered by anyone: no approval
   * prompt on any surface, no results submitted, and — because the batch never
   * reaches its claimed count — not even the results of the tools that did
   * run. The turn simply stops.
   *
   * Handing the claim over instead is not open to us: the server resumes
   * generation on any submission, so a batch answered in two pieces fails
   * provider validation on the unanswered ids. One runner has to answer all of
   * them, and this is the only runner that can.
   *
   * Called on unmount only. Safe when idle — a runner that owes nothing
   * submits nothing.
   */
  detach(): void {
    this.detached = true;

    const stranded = [
      ...(this.currentApproval ? [this.currentApproval] : []),
      ...this.pendingQueue,
    ];
    this.currentApproval = null;
    this.pendingQueue = [];
    for (const { toolUse } of stranded) {
      this.accumulatedResults.push(buildInterruptedResult(toolUse));
    }

    if (
      this.claimedToolCount > 0 &&
      this.accumulatedResults.length === this.claimedToolCount
    ) {
      this.completeBatch();
    }
  }

  /**
   * Drop the current batch's state.
   *
   * {@link handledToolUseIds} survives: forgetting which tool_uses already ran
   * is exactly what would let the next batch run one of them a second time.
   */
  reset(): void {
    this.pendingQueue = [];
    this.accumulatedResults = [];
    this.claimedToolCount = 0;
    this.batchSessionId = null;
    this.currentApproval = null;
    this.batchScope = {};
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /** The caller's deps, with this batch's shared scope attached. */
  private scoped(deps: ClientToolDeps): ClientToolDeps {
    return { ...deps, batchScope: this.batchScope };
  }

  /** True while any claimed tool_use is still queued, on screen, or running. */
  private hasUnsettledWork(): boolean {
    return (
      this.currentApproval !== null ||
      this.pendingQueue.length > 0 ||
      this.accumulatedResults.length < this.claimedToolCount
    );
  }

  private async executeAndRecord(
    toolUse: AgentThreadToolUseContent,
    tool: ClientTool,
    deps: ClientToolDeps,
  ): Promise<void> {
    const inputParams = toolUse.input ?? {};
    try {
      const startTime = performance.now();
      const result = await tool.execute(inputParams, deps);
      const runtimeMs = Math.round(performance.now() - startTime);
      this.accumulatedResults.push(buildToolResult(toolUse, result, runtimeMs));
    } catch (error) {
      // Classify here too, not just inside the tools: a throw that escapes
      // `execute` reaches the model as a tool result like any other, and an
      // unclassified one reads as retryable — the wrong guess for a write.
      this.accumulatedResults.push(
        buildToolResult(
          toolUse,
          toolError(describeToolFailure(error), {}, classifyToolFailure(error)),
          0,
        ),
      );
    }
  }

  private advanceQueue(): void {
    const next = this.pendingQueue.shift();
    if (next) {
      this.currentApproval = next;
      this.callbacks.onPendingApprovalChanged(next);
    } else {
      this.currentApproval = null;
      this.callbacks.onPendingApprovalChanged(null);
      if (this.accumulatedResults.length === this.claimedToolCount) {
        this.completeBatch();
      }
    }
  }

  private completeBatch(): void {
    if (this.batchSessionId) {
      this.callbacks.onBatchComplete(this.batchSessionId, [
        ...this.accumulatedResults,
      ]);
    }
  }
}
